// src/api/client.ts
import axios, {type AxiosError, type InternalAxiosRequestConfig} from 'axios';
import type {MutationRecord} from '@/lib/offline/queue';
import {getToken, clearToken} from '@/lib/auth-token';
import {resetAuthStore} from '@/store/auth';

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

/** Timeout for ordinary JSON calls — small bodies, should be quick. */
export const JSON_TIMEOUT_MS = 8000;

/**
 * Timeout for multipart uploads.
 *
 * A phone photo is several megabytes and on a mobile connection routinely
 * takes far longer than {@link JSON_TIMEOUT_MS} to send. Under the JSON
 * timeout axios aborted the upload mid-flight, which is what silently broke
 * photo-report capture on mobile: `POST /attachments/read-exif/` never
 * completed, `readExifMeta` swallowed the abort and returned null, and every
 * capture fell through to device GPS — indistinguishable from a photo that
 * genuinely carries no EXIF. The job-photo POST died the same way.
 */
export const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Timeout for the server-built XLSX report downloads.
 *
 * A workbook covers a whole period, so it legitimately takes longer than
 * {@link JSON_TIMEOUT_MS}. Under the JSON timeout a month's ledger or record
 * export failed on prod with "timeout of 8000ms exceeded" (2026-11-03) while
 * the server was still building it. Gunicorn's default 30 s worker timeout
 * (Dyna-ERP-API sets none of its own) stays the real bound; this only stops
 * the browser giving up first.
 */
export const REPORT_TIMEOUT_MS = 60_000;

/** True for a multipart body, i.e. a file upload rather than a JSON call. */
const isUpload = (data: unknown): boolean =>
  typeof FormData !== 'undefined' && data instanceof FormData;

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: JSON_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  // Array query params go on the wire as REPEATED keys —
  // `?status_in=approved&status_in=partially_approved`.
  //
  // Axios defaults to bracket notation (`status_in[]=approved&...`), which
  // django-ninja does not bind: it reads `status_in`, sees no such key, and
  // treats the filter as absent. That fails in the worst possible direction
  // for a filter — not an error, just unfiltered rows — and it is invisible
  // until someone notices the wrong count.
  //
  // `indexes: null` is axios's spelling for "repeat the key, no brackets, no
  // indices". Set on the instance rather than per call: every array param this
  // API grows needs the same treatment, and the day one is added without it is
  // the day a query silently stops filtering. First exercised by
  // `status_in` (DERP-10).
  paramsSerializer: {indexes: null},
});

// Request interceptor: attach Bearer token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Uploads get the longer budget. Applied centrally so every generated client
  // (photo capture, attachments, template assets) inherits it without each
  // call site having to remember.
  if (isUpload(config.data)) {
    config.timeout = UPLOAD_TIMEOUT_MS;
  }
  return config;
});

/** Auth endpoints where 401 is expected and handled by the auth flow, not the interceptor. */
const AUTH_401_SKIP_URLS = ['/auth/login', '/auth/whoami'];

export function handle401Error(error: AxiosError): Promise<never> {
  const url = error.config?.url ?? '';
  if (error.response?.status === 401 && !AUTH_401_SKIP_URLS.some((skip) => url.includes(skip))) {
    clearToken();
    resetAuthStore();
    // Hard navigation (full reload) so app state is reset cleanly.
    // The query param drives the session-expired banner on the login page.
    window.location.href = '/login?session_expired=true';
  }
  return Promise.reject(error);
}

// 401 interceptor: redirect to login on session expiry
api.interceptors.response.use((response) => response, handle401Error);

/** HTTP methods that represent mutations (writes) */
const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/** Headers that must never be persisted to IndexedDB (PDPA compliance) */
const HEADER_DENYLIST = new Set(['authorization', 'cookie', 'set-cookie']);

export function shouldQueueMutation(error: AxiosError): boolean {
  if (!error.config) return false;
  if (error.response) return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return false;
  // A multipart body cannot survive the queue: `serializeMutation` stringifies
  // it to "{}", so a replayed photo upload would post an empty form, and the
  // caller meanwhile receives the synthetic 202 as if it had succeeded. Fail
  // the upload honestly instead and let the worker retry it.
  if (isUpload(error.config.data)) return false;
  const method = error.config.method?.toLowerCase() ?? '';
  return MUTATION_METHODS.has(method);
}

export type SerializedMutation = Omit<MutationRecord, 'id' | 'createdAt' | 'retryCount' | 'status'>;

export function serializeMutation(config: InternalAxiosRequestConfig): SerializedMutation {
  const method = (config.method ?? 'POST').toUpperCase();
  const url = config.url ?? '';

  const headers: Record<string, string> = {};
  if (config.headers) {
    const raw = config.headers;
    if (typeof raw === 'object') {
      for (const [key, value] of Object.entries(raw)) {
        if (value != null && !HEADER_DENYLIST.has(key.toLowerCase())) {
          headers[key] = String(value);
        }
      }
    }
  }

  let body = '';
  if (config.data !== undefined && config.data !== null) {
    body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
  }

  return {method, url, headers, body};
}

export function installOfflineInterceptor(
  enqueue: (mutation: SerializedMutation) => Promise<string>,
  onQueued?: () => void,
): number {
  return api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (shouldQueueMutation(error) && error.config) {
        try {
          const serialized = serializeMutation(error.config);
          await enqueue(serialized);
          onQueued?.();
          return Promise.resolve({
            data: error.config.data ?? null,
            status: 202,
            statusText: 'QUEUED_OFFLINE',
            headers: {},
            config: error.config,
          });
        } catch {
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    },
  );
}

export default api;
